import React from 'react';
import { Link } from 'react-router-dom';

export type HubCardItem = {
  to?: string;
  title: string;
  description?: string;
  accent?: 'amber' | 'sky' | 'yellow' | 'violet' | 'cyan' | 'emerald' | 'slate';
  /** Carte visible mais non cliquable (ex. fonction référent manquante). */
  disabled?: boolean;
  disabledHint?: string;
};

const accentTitle: Record<NonNullable<HubCardItem['accent']>, string> = {
  amber: 'text-cockpit-amber',
  sky: 'text-cockpit-blue',
  yellow: 'text-cockpit-amber',
  violet: 'text-cockpit-violet',
  cyan: 'text-cockpit-blue',
  emerald: 'text-cockpit-green',
  slate: 'text-cockpit-text',
};

const accentBorder: Record<NonNullable<HubCardItem['accent']>, string> = {
  amber: 'hover:border-cockpit-amber/45',
  sky: 'hover:border-cockpit-blue/45',
  yellow: 'hover:border-cockpit-amber/45',
  violet: 'hover:border-cockpit-violet/45',
  cyan: 'hover:border-cockpit-blue/45',
  emerald: 'hover:border-cockpit-green/45',
  slate: 'hover:border-cockpit-line',
};

const cardShell =
  'rounded-md border border-cockpit-line/70 bg-cockpit-panel/70 p-5 backdrop-blur-sm transition-all';

type PanelHubGridProps = {
  /** Titre optionnel — le layout affiche déjà le nom de section dans le header. */
  title?: string;
  description?: string;
  cards: HubCardItem[];
  columns?: 1 | 2;
  footer?: React.ReactNode;
};

/** Grille de cartes pour hubs panel (CM, admin, modo, scénariste, référents…). */
const PanelHubGrid: React.FC<PanelHubGridProps> = ({ title, description, cards, columns = 2, footer }) => {
  if (!cards.length) {
    return (
      <div className={`${cardShell} border-dashed p-8 text-center text-sm text-cockpit-muted`}>
        Aucun outil disponible avec vos droits actuels.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {title || description ? (
        <div>
          {title ? <h1 className="text-xl font-bold tracking-tight text-cockpit-text lg:hidden">{title}</h1> : null}
          {description ? (
            <p className={`max-w-2xl text-sm leading-relaxed text-cockpit-text-2 ${title ? 'mt-2' : ''}`}>{description}</p>
          ) : null}
        </div>
      ) : null}

      <div className={columns === 2 ? 'grid gap-4 sm:grid-cols-2' : 'grid gap-4'}>
        {cards.map((c) => {
          const a = c.accent || 'slate';
          const content = (
            <>
              <div className={`text-sm font-semibold ${c.disabled ? 'text-cockpit-muted' : accentTitle[a]}`}>{c.title}</div>
              {c.description ? (
                <p className="mt-2 text-xs leading-relaxed text-cockpit-text-2">{c.description}</p>
              ) : null}
              {c.disabled && c.disabledHint ? (
                <p className="mt-2 text-[11px] text-cockpit-muted">{c.disabledHint}</p>
              ) : c.to ? (
                <span className="mt-4 inline-flex items-center gap-1 text-[11px] font-medium text-cockpit-text-2 group-hover:text-cockpit-text">
                  Ouvrir
                  <span aria-hidden>→</span>
                </span>
              ) : null}
            </>
          );

          if (c.disabled || !c.to) {
            return (
              <div
                key={c.title}
                className={`${cardShell} cursor-not-allowed border-cockpit-line-soft/70 opacity-75`}
                title={c.disabledHint}
              >
                {content}
              </div>
            );
          }

          return (
            <Link
              key={c.to + c.title}
              to={c.to}
              className={`group ${cardShell} hover:bg-cockpit-panel-2/80 ${accentBorder[a]}`}
            >
              {content}
            </Link>
          );
        })}
      </div>

      {footer ? <div className="pt-1">{footer}</div> : null}
    </div>
  );
};

export default PanelHubGrid;
